import {
	type GetImpulseFormInput,
	type GetImpulseFormInputSetter,
	ImpulseFormList,
	ImpulseFormShape,
} from "~/tools/impulse-form";
import { setFormElementsInput } from "~/tools/impulse-form/set-form-elements-input";
import { isPresent } from "~/tools/is-present";
import type { Partial } from "~/tools/partial";

import { WidgetCalculation } from "./calculation/form-state";
import { WidgetColor } from "./color/form-state";
import { WidgetDecimals } from "./decimals/form-state";
import { WidgetFontSize } from "./font-size/form-state";
import { WidgetMetricLabel } from "./metric-label/form-state";
import { WidgetSparkline } from "./sparkline/form-state";
import { type WidgetThresholdsColor, WidgetThresholdsStep } from "./thresholds/form-state";
import { WidgetUnit } from "./unit/form-state";
import type { WidgetFormSync } from "./sync";

type StatChartFormState = ImpulseFormShape<{
	unit: WidgetUnit;
	decimals: WidgetDecimals;
	sparkline: WidgetSparkline;
	calculation: WidgetCalculation;
	metricLabel: WidgetMetricLabel;
	fontSize: WidgetFontSize;
	color: WidgetThresholdsColor;
	thresholds: ImpulseFormList<WidgetThresholdsStep>;
}>;

type Input = GetImpulseFormInput<StatChartFormState>;

const DEFAULT = {
	unit: WidgetUnit.default,
	decimals: WidgetDecimals.default,
	sparkline: WidgetSparkline.default,
	calculation: WidgetCalculation.default,
	metricLabel: WidgetMetricLabel.default,
	fontSize: WidgetFontSize.default,
	color: WidgetColor.healthy,
	thresholds: [],
} satisfies Input;

function init(initial?: Partial<Input, "deep">): StatChartFormState {
	return ImpulseFormShape({
		unit: WidgetUnit.init(initial?.unit),
		decimals: WidgetDecimals.init(initial?.decimals),
		sparkline: WidgetSparkline.init(initial?.sparkline),
		calculation: WidgetCalculation.init(initial?.calculation),
		metricLabel: WidgetMetricLabel.init(initial?.metricLabel),
		fontSize: WidgetFontSize.init(initial?.fontSize),
		color: WidgetColor.init(initial?.color ?? DEFAULT.color),
		thresholds: ImpulseFormList((initial?.thresholds ?? DEFAULT.thresholds).map(WidgetThresholdsStep.init)),
	});
}

function setInput(
	{ unit, decimals, sparkline, calculation, metricLabel, fontSize, color, thresholds }: Partial<Input, "deep">,
	state: StatChartFormState,
): void {
	state.setInput({
		unit,
		decimals,
		sparkline,
		calculation,
		metricLabel,
		fontSize,
		color,
	});

	if (thresholds != null) {
		setFormElementsInput(
			(threshold) => WidgetThresholdsStep.init(threshold),
			thresholds.filter(isPresent),
			state.fields.thresholds,
		);
	}
}

function fromSync({
	unit,
	decimals,
	sparkline,
	calculation,
	metricLabel,
	fontSize,
	color,
	thresholds,
}: WidgetFormSync) {
	return {
		unit,
		decimals,
		sparkline,
		calculation,
		metricLabel,
		fontSize,
		color,
		thresholds: thresholds?.steps,
	} satisfies GetImpulseFormInputSetter<StatChartFormState>;
}

function toSync({ unit, decimals, sparkline, calculation, metricLabel, fontSize, color, thresholds }: Input) {
	return {
		unit,
		decimals,
		sparkline,
		calculation,
		metricLabel,
		fontSize,
		color,
		thresholds: {
			steps: thresholds,
		},
	} satisfies WidgetFormSync;
}

const StatChartFormState = {
	default: DEFAULT,
	init,
	setInput,
	toSync,
	fromSync,
} as const;

export { StatChartFormState };
